import { isDcfOpen } from './dcfStatus';

/** Yellow highlight: DCF status Open (Visit Report field row). */
export const isFieldDcfOpen = (field) => isDcfOpen(field?.vDCFStatus ?? field?.VDCFStatus);

const normStage = (s) =>
  (s ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

/**
 * Client guard aligned with API GetDcfResolveStageMismatchMessage (sent-to vs header).
 * Branch DCFs (sent = assigned review stage) resolve only at that header stage.
 * General DCFs (sent = "In Progress") are CRA-routed via iSubmitDCFToCRA=1 and are
 * not stage-gated here — server IsEditable / role checks govern release-based authorization.
 * Empty vDCFSentToReviewStage → allowed (legacy / untagged).
 * @param {unknown} [headerCurrentStageCode] retained for API compatibility (no longer needed by the gate).
 * @param {boolean} [apiHeaderStageInProgress] retained for API compatibility (no longer needed by the gate).
 */
export function openDcfResolutionAllowedAtHeaderStage(
  field,
  headerStageLabel,
  headerCurrentStageCode, // eslint-disable-line no-unused-vars
  apiHeaderStageInProgress // eslint-disable-line no-unused-vars
) {
  if (!isDcfOpen(field?.vDCFStatus ?? field?.VDCFStatus)) return true;
  const sentRaw = (
    field?.vDCFResolveStage ??
    field?.VDCFResolveStage ??
    field?.vDCFSentToReviewStage ??
    field?.VDCFSentToReviewStage ??
    ''
  )
    .toString()
    .trim();
  if (!sentRaw) return true;
  const sent = normStage(sentRaw);
  const ip = normStage('In Progress');
  if (sent === ip) return true;
  return normStage((headerStageLabel ?? '').toString().trim()) === sent;
}

/**
 * Client mirror of CRFPageService.OpenDcfRowSubmitSatisfied (ValidateSubmitOnly / submit, before e-sign).
 * Each open Reviewer's Comment row must have the CRF Answer value changed from persisted (normalized).
 * Comments column and DCF modal remarks do not satisfy submit by themselves.
 * @param {object} savedField persisted row (savedFields item)
 * @param {object|null|undefined} submittedField same field from collectFormData(); missing key behaves like server TryGetValue miss
 * @param {(raw: unknown) => string} normalizeSubmitAnswer aligns with CrfSubmitNormalizeAnswer + local placeholder rules
 */
/**
 * Client guard aligned with API GetDcfCloseStageMismatchMessage.
 * Raiser may close Resolved DCF at the raised stage or tier-routed sent-to stage (viewing lane or header).
 */
export function dcfCloseAllowedAtViewingStage(dcfInfo, viewingStageLabel, headerStageLabel) {
  const sentRaw = (dcfInfo?.sentToReviewStage ?? '').toString().trim();
  const raisedRaw = (dcfInfo?.raisedStage ?? '').toString().trim();
  const view = normStage((viewingStageLabel ?? '').toString().trim());
  const hdr = normStage((headerStageLabel ?? '').toString().trim());
  const ip = normStage('In Progress');

  if (sentRaw) {
    const sent = normStage(sentRaw);
    if (sent !== ip) {
      if (sent === view || sent === hdr) return true;
      if (raisedRaw) {
        const raised = normStage(raisedRaw);
        if (raised === view || raised === hdr) return true;
      }
      return false;
    }
  }

  if (!raisedRaw) return true;
  const raised = normStage(raisedRaw);
  return raised === view || raised === hdr;
}

export function openDcfRowSubmitSatisfiedClient(savedField, submittedField, normalizeSubmitAnswer) {
  if (submittedField == null) return false;
  const norm = typeof normalizeSubmitAnswer === 'function' ? normalizeSubmitAnswer : (raw) => String(raw ?? '').trim();
  const curN = norm(submittedField.vFieldValue ?? submittedField.VFieldValue);
  const origN = norm(savedField?.vFieldValue ?? savedField?.VFieldValue);
  return origN !== curN;
}
